import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  CheckCircle,
  CreditCard,
  FileCheck,
  FileText,
  Layers,
  LineChart,
  Network,
  RefreshCw,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { ContactSection } from '../components/ContactSection';
import o2cPreviewImage from '../components/images/O2C.png';

const journeySteps = [
  {
    title: 'Order validation',
    label: 'Confirm the commercial record',
    desc: 'Validate customer, product, contract, tax, entitlement, and fulfillment details before billing begins.',
    icon: FileCheck,
  },
  {
    title: 'Billing readiness',
    label: 'Prepare invoiceable events',
    desc: 'Convert orders, usage, subscriptions, renewals, and service changes into governed billing schedules and charge events.',
    icon: Layers,
  },
  {
    title: 'Invoice generation',
    label: 'Create accurate invoices',
    desc: 'Automate recurring, usage-based, milestone, and one-time invoice creation with tax and discount logic applied.',
    icon: FileText,
  },
  {
    title: 'Payment collection',
    label: 'Move cash faster',
    desc: 'Coordinate payment links, gateway status, collection reminders, dunning actions, and customer payment visibility.',
    icon: CreditCard,
  },
  {
    title: 'Reconciliation',
    label: 'Match cash to ledger',
    desc: 'Match payments, credits, refunds, adjustments, and settlement records against invoices and finance systems.',
    icon: RefreshCw,
  },
  {
    title: 'Revenue reporting',
    label: 'Close with confidence',
    desc: 'Sync revenue, AR, taxes, aging, and GL movements into dashboards and ERP workflows for faster financial control.',
    icon: LineChart,
  },
];

const valueCards = [
  {
    title: 'Cleaner revenue operations',
    desc: 'Orders, invoice schedules, payments, collections, and accounting handoffs stay connected in one operating path.',
    icon: CheckCircle,
  },
  {
    title: 'Billing accuracy at scale',
    desc: 'Automated validation reduces leakage from missing charges, tax mismatches, duplicate invoices, and delayed adjustments.',
    icon: ShieldCheck,
  },
  {
    title: 'Faster cash conversion',
    desc: 'Collections, reconciliation, and finance visibility improve cash flow without adding manual finance workload.',
    icon: Zap,
  },
];

const operatingSignals = [
  'Order-to-invoice status tracking',
  'Tax, discount, and credit validation',
  'Subscription and usage billing schedules',
  'Payment gateway and dunning visibility',
  'Cash application and reconciliation',
  'AR aging, revenue, and GL dashboards',
];

export default function OrderToCashPage() {
  return (
    <main className="bg-white text-slate-950">
      <section className="relative overflow-hidden bg-slate-950 px-6 pb-12 pt-32 text-white md:px-12 md:pb-16">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(255,93,34,0.18),transparent_30%),radial-gradient(circle_at_12%_78%,rgba(14,165,233,0.12),transparent_28%)]" />
          <div className="absolute inset-0 opacity-[0.055] bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:44px_44px]" />
        </div>

        <div className="relative z-10 mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div>
            <div className="inline-flex rounded-full border border-[#ff5d22]/25 bg-[#ff5d22]/10 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff8a5c]">
              Platform Capability
            </div>
            <h1 className="mt-6 max-w-3xl text-3xl font-black leading-[1.02] tracking-tighter text-white md:text-5xl lg:text-6xl">
              Order to Cash, without <span className="text-[#ff5d22]">revenue leakage.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-sm font-bold leading-relaxed text-white/80 md:text-base">
              AppGallop connects confirmed orders to billing, invoicing, payments, collections, reconciliation, and revenue reporting. Finance and operations teams get a governed path from commercial commitment to cash realization.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href="#order-to-cash-contact" className="inline-flex min-h-12 items-center justify-center rounded-xl bg-[#ff5d22] px-4 text-[10px] font-black uppercase tracking-widest text-white shadow-xl shadow-[#ff5d22]/20 transition-all hover:bg-[#e34f1d]">
                Discuss Order to Cash <ArrowRight size={14} className="ml-2" />
              </a>
              <Link to="/products/uc-billing" className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/15 bg-white/[0.04] px-6 text-[10px] font-black uppercase tracking-widest text-white/75 transition-all hover:border-white/35 hover:text-white">
                View Billing Engine
              </Link>
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-3 shadow-2xl shadow-black/30">
            <div className="overflow-hidden rounded-[1.35rem] border border-white/10 bg-black/30">
              <img src={o2cPreviewImage} alt="Order to Cash workflow preview" className="h-full w-full object-fill" />
            </div>
            <div className="grid grid-cols-3 border-t border-white/10">
              {[
                { value: '98%', label: 'COLLECTION EFFICIENCY' },
                { value: '100%', label: 'REVENUE VISIBILITY' },
                { value: '99.9%', label: 'BILLING ACCURACY' },
              ].map((stat) => (
                <div key={stat.label} className="border-r border-white/10 px-4 py-4 last:border-r-0">
                  <div className="text-xl font-black text-white">{stat.value}</div>
                  <div className="mt-1 text-[9px] font-black uppercase tracking-widest leading-relaxed text-white/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-10 md:px-12 md:py-14">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex rounded-full border border-[#ff5d22]/15 bg-[#ff5d22]/5 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22]">
              Workflow map
            </div>
            <h2 className="mt-5 text-3xl font-black tracking-tighter text-slate-950 md:text-5xl">
              From order validation to <span className="text-[#ff5d22]">cash realization.</span>
            </h2>
            <p className="mt-4 text-sm font-bold leading-relaxed text-black/55 md:text-base">
              A structured Order to Cash path keeps operations, billing, collections, tax, and finance aligned after every order is confirmed.
            </p>
          </div>

          <div className="mt-8 overflow-hidden rounded-[1.75rem] border border-black/6 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
            <div className="border-b border-black/6 bg-slate-50 px-5 py-4 md:px-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[0.2em] text-black/35">AppGallop O2C operating path</div>
                  <h3 className="mt-1 text-xl font-black tracking-tight text-slate-950">Six controlled stages, one revenue trail.</h3>
                </div>
                <div className="inline-flex w-fit items-center rounded-full border border-[#ff5d22]/20 bg-white px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-[#ff5d22]">
                  Order ready to cash ready
                </div>
              </div>
            </div>

            <div className="p-5 md:p-7">
              <div className="grid gap-3 lg:grid-cols-6">
                {journeySteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.title} className="relative">
                      {index < journeySteps.length - 1 && (
                        <div className="absolute left-[calc(50%+34px)] top-8 hidden h-px w-[calc(100%-42px)] bg-gradient-to-r from-[#ff5d22]/45 to-black/8 lg:block" />
                      )}
                      <div className="group relative z-10 flex h-full flex-col rounded-[1.15rem] border border-black/6 bg-slate-50 p-4 transition-all hover:-translate-y-1 hover:border-[#ff5d22]/25 hover:bg-white hover:shadow-xl">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#ff5d22]/15 bg-white text-[#ff5d22] shadow-sm transition-colors group-hover:bg-[#ff5d22] group-hover:text-white">
                            <Icon size={20} />
                          </div>
                          <div className="text-[10px] font-black uppercase tracking-widest text-black/25">0{index + 1}</div>
                        </div>
                        <h4 className="mt-4 text-sm font-black leading-tight tracking-tight text-slate-950">{step.title}</h4>
                        <div className="mt-2 h-1 w-8 rounded-full bg-[#ff5d22]" />
                        <p className="mt-3 text-[11px] font-bold leading-relaxed text-black/50">{step.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {journeySteps.map((step, index) => (
                  <div key={`${step.title}-detail`} className="rounded-xl border border-black/6 bg-white px-4 py-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#ff5d22]/10 text-[10px] font-black text-[#ff5d22]">
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="text-sm font-black leading-snug text-slate-950">{step.title}</h4>
                        <p className="mt-1.5 text-xs font-bold leading-relaxed text-black/52">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 px-6 py-10 md:px-12 md:py-4">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 grid gap-4 md:grid-cols-3">
            {valueCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="capability-value-card group overflow-hidden rounded-[1.25rem] border border-black/6 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)] transition-all duration-500 hover:-translate-y-1 hover:border-[#ff5d22]/25 hover:shadow-[0_24px_80px_rgba(255,93,34,0.14)]"
                >
                  <div className="relative z-10 flex h-11 w-11 items-center justify-center rounded-xl bg-[#ff5d22]/10 text-[#ff5d22] transition-all duration-500 group-hover:scale-105 group-hover:bg-[#ff5d22] group-hover:text-white">
                    <Icon size={20} />
                  </div>
                  <h3 className="relative z-10 mt-5 text-xl font-black tracking-tight text-slate-950 transition-colors duration-500 group-hover:text-[#ff5d22]">{card.title}</h3>
                  <p className="relative z-10 mt-3 text-sm font-bold leading-relaxed text-black/52">{card.desc}</p>                  
                </div>
              );
            })}
          </div>

          <div className="mt-6 overflow-hidden rounded-[1.5rem] border border-black/6 bg-white shadow-sm">
            <div className="grid gap-0 lg:grid-cols-[0.75fr_1.25fr]">
              <div className="bg-slate-950 p-7 text-white md:p-8">
                <div className="inline-flex rounded-full border border-[#ff5d22]/25 bg-[#ff5d22]/10 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff8a5c]">
                  Operating signals
                </div>
                <h2 className="mt-5 text-3xl font-black tracking-tighter text-white">
                  Revenue visibility your finance team can <span className="text-[#ff5d22]">close with.</span>
                </h2>
                <p className="mt-4 text-sm font-bold leading-relaxed text-white/80">
                  AppGallop turns every order, invoice, payment, adjustment, and reconciliation event into trackable finance data.
                </p>
              </div>
              <div className="grid gap-3 p-6 sm:grid-cols-2 md:p-8">
                {operatingSignals.map((signal) => (
                  <div key={signal} className="flex items-start gap-3 rounded-xl border border-black/6 bg-slate-50 px-4 py-3">
                    <CheckCircle size={17} className="mt-0.5 shrink-0 text-[#ff5d22]" />
                    <span className="text-sm font-black leading-snug text-slate-950">{signal}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[
              { label: 'Billing', value: 'Invoice-ready events', icon: FileText },
              { label: 'Collections', value: 'Payment status control', icon: CreditCard },
              { label: 'Finance', value: 'Ledger-ready reconciliation', icon: Network },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="rounded-xl border border-black/6 bg-white px-5 py-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#ff5d22]/10 text-[#ff5d22]">
                      <Icon size={18} />
                    </div>
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-widest text-black/35">{item.label}</div>
                      <div className="mt-1 text-sm font-black text-slate-950">{item.value}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-6 py-10 md:px-12 md:py-14">
        <div className="mx-auto grid max-w-7xl gap-8 rounded-[1.75rem] border border-black/6 bg-white p-7 shadow-sm md:p-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <div className="inline-flex rounded-full border border-[#ff5d22]/15 bg-[#ff5d22]/5 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22]">
              AI assistance
            </div>
            <h2 className="mt-5 text-3xl font-black tracking-tighter text-slate-950 md:text-5xl">
              Built for finance teams who need <span className="text-[#ff5d22]">speed and auditability.</span>
            </h2>
            <p className="mt-4 text-sm font-bold leading-relaxed text-black/55">
              AI can surface billing exceptions, summarize payment delays, detect mismatches, and recommend next actions. Finance teams retain control over approvals, adjustments, write-offs, and period close.
            </p>
          </div>
          <div className="grid gap-3">
            {[
              'AI-assisted billing exception detection',
              'Payment delay and collection risk summaries',
              'Duplicate invoice, missing charge, and mismatch alerts',
              'Invoice, payment, adjustment, and ledger audit trail',
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-xl border border-black/6 bg-slate-50 px-4 py-3">
                <Bot size={18} className="mt-0.5 shrink-0 text-[#ff5d22]" />
                <span className="text-sm font-bold leading-relaxed text-black/58">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div id="order-to-cash-contact">
        <ContactSection />
      </div>
    </main>
  );
}
